import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from 'services'
import { useSnackbar } from 'contexts'
import { assertOk, RolesMatrixResponse } from './useRolesMatrix'

// ── Query keys ───────────────────────────────────────────────────────────────

export const internalRolesMatrixQueryKeys = {
  matrix: () => ['roles', 'internal', 'matrix'] as const,
}

// Internal permission-group display order — mirrors the internal sidebar
// (Sidebar.tsx INTERNAL_MENU). The matrix sorts its internal groups by each group's
// `surface` index here, so the permission groups follow the sidebar order. Reorder this
// array (kept in sync with the sidebar) to reorder the groups in one place.
const INTERNAL_SIDEBAR_SURFACE_ORDER = [
  // VRG-685 — Spencer's sidebar order (2026-10-08): Analytics, GTM, Accounts, Commerce,
  // Operations, Tools. Accounts' groups (companies, subscriptions) now come before Commerce's
  // (packages, orders, invoices), matching the sidebar rather than the older list order.
  'analytics',
  'gtm',
  'companies',
  'subscriptions',
  'packages',
  'orders',
  'invoices',
  'users',
  // VRG-226 — /internal/support sits in Operations, after Team.
  'support',
]
const surfaceRank = (g: { surface?: string }) => {
  const i = INTERNAL_SIDEBAR_SURFACE_ORDER.indexOf(g.surface ?? '')
  return i < 0 ? Number.MAX_SAFE_INTEGER : i
}

// ── Query hook ────────────────────────────────────────────────────────────────

/**
 * Fetches the internal roles permissions matrix from GET /roles/internal/matrix.
 * Mirrors useRolesMatrix but scoped to internal (Vera-staff) roles and
 * internal-scope permission groups.
 */
export const useInternalRolesMatrix = () => {
  return useQuery<RolesMatrixResponse>({
    queryKey: internalRolesMatrixQueryKeys.matrix(),
    queryFn: async () => {
      const response = await api.get('/roles/internal/matrix')
      const body = response.data
      const groups = Array.isArray(body?.groups) ? [...body.groups] : []
      // Sort the internal permission groups by the sidebar surface order.
      groups.sort((a, b) => surfaceRank(a) - surfaceRank(b))
      return {
        roles: Array.isArray(body?.roles) ? body.roles : [],
        groups,
      }
    },
    staleTime: 1000 * 60 * 2,
  })
}

// ── Mutation hook ─────────────────────────────────────────────────────────────

interface UpdateInternalRolePermissionsPayload {
  roleId: string
  permissionIds: string[]
}

/**
 * Replaces the full permission set for a global internal role.
 * PUT /roles/internal/:roleId/permissions
 */
export const useUpdateInternalRolePermissions = () => {
  const queryClient = useQueryClient()
  const { open: notify } = useSnackbar()

  return useMutation<void, Error, UpdateInternalRolePermissionsPayload>({
    mutationFn: ({ roleId, permissionIds }) =>
      api
        .put(`/roles/internal/${roleId}/permissions`, { permissionIds })
        .then(assertOk)
        .then(() => undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: internalRolesMatrixQueryKeys.matrix() })
      notify({ type: 'success', title: 'Permissions updated' })
    },
    onError: () => {
      notify({ type: 'error', title: 'Failed to update permissions' })
    },
  })
}
